import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';

const Leaderboard = ({ wallets = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [copiedAddress, setCopiedAddress] = useState('');

  // Sort wallets by paperhanded value (descending) - matches the wallet analysis data
  const sortedWallets = useMemo(() => {
    return [...wallets].sort((a, b) => {
      // Primary sort: total missed gains (paperhanded value)
      const aValue = a.paperhandedValue || a.totalMissedGains || 0;
      const bValue = b.paperhandedValue || b.totalMissedGains || 0;
      
      // If values are equal, sort by Jitter Score (higher = more jittery)
      if (aValue === bValue) {
        const aJitter = a.jitterScore || 0;
        const bJitter = b.jitterScore || 0;
        return bJitter - aJitter;
      }
      
      return bValue - aValue;
    });
  }, [wallets]);

  const totalPages = Math.ceil(sortedWallets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentWallets = sortedWallets.slice(startIndex, endIndex);
  const totalWallets = sortedWallets.length;

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    setCopiedAddress(address);
    setTimeout(() => setCopiedAddress(''), 2000);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) {
      return (
        <span className="text-2xl">🥇</span>
      );
    } else if (rank === 2) {
      return (
        <span className="text-2xl">🥈</span>
      );
    } else if (rank === 3) {
      return (
        <span className="text-2xl">🥉</span>
      );
    }
    return (
      <span className="text-white font-bold text-lg">{rank}</span>
    );
  };

  const formatPaperhandedValue = (wallet) => {
    // Use paperhandedValue (total missed gains) - matches wallet analysis
    const value = wallet.paperhandedValue || wallet.totalMissedGains || 0;
    const solPrice = 150; // Default SOL price, should be dynamic
    const solValue = value / solPrice;
    const usdValue = value;
    
    // Format USD value with commas for thousands and period for decimals
    const formattedUsd = `$${usdValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    
    // Format SOL value with commas for thousands and period for decimals
    const formattedSol = solValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 });
    
    return {
      sol: formattedSol,
      usd: formattedUsd,
      rawValue: value, // For debugging/verification
      rawSol: solValue // Raw SOL value
    };
  };

  return (
    <div className="bg-[#2a2a2a] rounded-lg border border-[#404040] overflow-hidden">
      {/* Table Header */}
      <div className="hidden sm:grid grid-cols-[100px_1fr_300px] gap-4 px-4 sm:px-6 py-4 border-b border-[#404040] bg-[#1a1a1a]">
        <div className="text-gray-400 text-sm font-semibold">RANK</div>
        <div className="text-gray-400 text-sm font-semibold">WALLET ADDRESS</div>
        <div className="text-gray-400 text-sm font-semibold text-right">PAPERHANDED VALUE</div>
      </div>
      
      {/* Table Rows */}
      <div className="divide-y divide-[#404040]">
        {currentWallets.length > 0 ? (
          currentWallets.map((wallet, index) => {
            const rank = startIndex + index + 1;
            const formatted = formatPaperhandedValue(wallet);
            const walletAddress = wallet.walletAddress || wallet.address || '';
            
            return (
              <div
                key={walletAddress || index}
                className="grid grid-cols-1 sm:grid-cols-[100px_1fr_300px] gap-3 sm:gap-4 items-start sm:items-center px-4 sm:px-6 py-4 hover:bg-[#1a1a1a] transition-all"
              >
                {/* Rank */}
                <div className="flex items-center">
                  <span className="sm:hidden text-gray-400 text-xs font-semibold mr-2">Rank:</span>
                  {getRankIcon(rank)}
                </div>
                
                {/* Wallet Address */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="sm:hidden text-gray-400 text-xs font-semibold mr-2">Wallet:</span>
                  <span className="text-white font-mono text-xs sm:text-sm break-all">
                    {walletAddress ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}` : 'Unknown'}
                  </span>
                  {walletAddress && (
                    <button
                      onClick={() => copyAddress(walletAddress)}
                      className="text-gray-400 hover:text-green-400 transition flex-shrink-0"
                      title="Copy wallet address"
                    >
                      {copiedAddress === walletAddress ? (
                        <Check size={14} className="text-green-400" />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  )}
                </div>
                
                {/* Paperhanded Value - Exact amounts (no approximations) */}
                <div className="flex items-center justify-start sm:justify-end gap-2 flex-shrink-0">
                  <span className="sm:hidden text-gray-400 text-xs font-semibold">Value:</span>
                  <span className="text-white font-semibold text-xs sm:text-sm font-mono flex items-center gap-1">
                    {formatted.sol}{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 text-base sm:text-lg font-bold" style={{ WebkitBackgroundClip: 'text', backgroundClip: 'text' }}>
                      ☰
                    </span>
                  </span>
                  <span className="text-gray-500 text-xs sm:text-sm font-mono">
                    ({formatted.usd})
                  </span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No jeeters found yet. Be the first!</p>
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {totalWallets > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-[#404040] bg-[#1a1a1a]">
          <span className="text-gray-500 text-xs sm:text-sm">
            Showing: {Math.min(endIndex, totalWallets)} of {totalWallets}
          </span>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg transition-all text-sm flex items-center justify-center gap-2 ${
                currentPage === 1
                  ? 'bg-[#1a1a1a] text-gray-500 cursor-not-allowed'
                  : 'bg-[#2a2a2a] hover:bg-[#404040] text-white border border-[#404040]'
              }`}
            >
              <ChevronLeft size={16} />
              Back
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className={`flex-1 sm:flex-none px-4 py-2 rounded-lg transition-all text-sm flex items-center justify-center gap-2 ${
                currentPage === totalPages
                  ? 'bg-[#1a1a1a] text-gray-500 cursor-not-allowed'
                  : 'bg-[#2a2a2a] hover:bg-[#404040] text-green-500 border border-[#404040]'
              }`}
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
